/**
 * QuotesInbox.tsx — Incoming supplier quotes. Quotes are uploaded as files and
 * AI-read by the backend (async): while `extractionStatus === "extracting"` a
 * spinner shows; on "failed" the error surfaces and the numeric cells become
 * manually editable. Accepting / rejecting a quote flows the margin update
 * automatically (useUpdateQuote invalidates the strategy query upstream).
 * Every number comes from the server Quote — nothing is fabricated here.
 */
import { useRef, useState } from "react";

import { I } from "../../components/icons";
import { fmtEur } from "../../lib/format";
import type { Quote, QuoteStatus, QuoteUpdateInput } from "../../lib/strategyApi";

function EditNum({
  value,
  onSave,
}: {
  value: number | null;
  onSave: (v: number) => void;
}): JSX.Element {
  const [v, setV] = useState<string>(value != null ? String(Math.round(value)) : "");
  return (
    <input
      className="input t-num"
      value={v}
      placeholder="—"
      onChange={(e) => setV(e.target.value.replace(/\D/g, ""))}
      onBlur={() => {
        const n = Number(v) || 0;
        if (n !== (value ?? 0)) onSave(n);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
      style={{
        textAlign: "right",
        height: 26,
        fontSize: 12,
        padding: "0 8px",
        width: "100%",
        fontFamily: "var(--font-mono)",
      }}
    />
  );
}

function StatusBadge({ status }: { status: QuoteStatus }): JSX.Element {
  if (status === "accepted")
    return (
      <span className="pill pill-ok">
        <I.Check size={10} /> Accettata
      </span>
    );
  if (status === "rejected")
    return (
      <span className="pill pill-err">
        <I.X size={10} /> Rifiutata
      </span>
    );
  return (
    <span className="pill">
      <I.Clock size={10} /> In attesa
    </span>
  );
}

function QuoteRow({
  q,
  onSetStatus,
  onDelete,
  onEditField,
}: {
  q: Quote;
  onSetStatus: (id: string, status: QuoteStatus) => void;
  onDelete: (id: string) => void;
  onEditField: (id: string, patch: QuoteUpdateInput) => void;
}): JSX.Element {
  const extracting = q.extractionStatus === "extracting";
  const failed = q.extractionStatus === "failed";

  return (
    <tr>
      <td>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {extracting && <span className="spinner" />}
          {failed && <I.Alert size={14} style={{ color: "var(--c-err)", flexShrink: 0 }} />}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 500 }}>{q.supplierName}</div>
            {extracting && (
              <div style={{ fontSize: 11, color: "var(--c-ai)", marginTop: 1 }}>
                Lettura AI in corso…
              </div>
            )}
            {failed && (
              <div style={{ fontSize: 11, color: "var(--c-err)", marginTop: 1 }}>
                {q.extractionError ?? "Lettura AI non riuscita — inserisci i valori a mano."}
              </div>
            )}
            {!extracting && !failed && q.sourceFilename && (
              <div
                style={{
                  fontSize: 11,
                  color: "var(--c-ink-3)",
                  marginTop: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 220,
                }}
              >
                {q.sourceFilename}
              </div>
            )}
          </div>
        </div>
      </td>
      <td style={{ color: "var(--c-ink-2)" }}>{q.categoryName ?? "—"}</td>
      <td className="t-num" style={{ textAlign: "right" }}>
        {failed ? (
          <EditNum value={q.materialCost} onSave={(v) => onEditField(q.id, { materialCost: v })} />
        ) : q.materialCost != null ? (
          fmtEur(q.materialCost)
        ) : (
          "—"
        )}
      </td>
      <td className="t-num" style={{ textAlign: "right" }}>
        {failed ? (
          <EditNum value={q.laborCost} onSave={(v) => onEditField(q.id, { laborCost: v })} />
        ) : q.laborCost != null ? (
          fmtEur(q.laborCost)
        ) : (
          "—"
        )}
      </td>
      <td
        className="t-num"
        style={{ textAlign: "right", fontWeight: 600, fontFamily: "var(--font-mono)" }}
      >
        {failed ? (
          <EditNum value={q.total} onSave={(v) => onEditField(q.id, { total: v })} />
        ) : (
          fmtEur(q.total)
        )}
      </td>
      <td
        className="t-num"
        style={{
          textAlign: "right",
          color:
            q.vsRegion == null ? "var(--c-ink-3)" : q.vsRegion < 0 ? "var(--c-ok)" : "var(--c-err)",
          fontWeight: 500,
        }}
      >
        {q.vsRegion == null ? "—" : `${q.vsRegion >= 0 ? "+" : ""}${q.vsRegion.toFixed(1)}%`}
      </td>
      <td>
        <StatusBadge status={q.status} />
      </td>
      <td>
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
          {!extracting && q.status === "pending" && (
            <>
              <button
                className="btn btn-sm"
                style={{
                  background: "var(--c-ok-soft)",
                  color: "var(--c-ok)",
                  borderColor: "rgba(47,125,78,0.20)",
                }}
                onClick={() => onSetStatus(q.id, "accepted")}
              >
                <I.Check size={12} /> Accetta
              </button>
              <button className="btn btn-sm" onClick={() => onSetStatus(q.id, "rejected")}>
                Rifiuta
              </button>
            </>
          )}
          {!extracting && q.status !== "pending" && (
            <button className="btn btn-ghost btn-sm" onClick={() => onSetStatus(q.id, "pending")}>
              Riapri
            </button>
          )}
          <button
            className="btn btn-ghost btn-icon btn-sm"
            title="Elimina offerta"
            onClick={() => onDelete(q.id)}
          >
            <I.Trash size={13} />
          </button>
        </div>
      </td>
    </tr>
  );
}

interface QuotesInboxProps {
  quotes: Quote[];
  loading: boolean;
  uploading: boolean;
  onUpload: (file: File) => void;
  onSetStatus: (id: string, status: QuoteStatus) => void;
  onDelete: (id: string) => void;
  onEditField: (id: string, patch: QuoteUpdateInput) => void;
}

export function QuotesInbox({
  quotes,
  loading,
  uploading,
  onUpload,
  onSetStatus,
  onDelete,
  onEditField,
}: QuotesInboxProps): JSX.Element {
  const fileRef = useRef<HTMLInputElement>(null);

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 14px",
          borderBottom: "1px solid var(--c-line)",
        }}
      >
        <div style={{ fontSize: 12, color: "var(--c-ink-3)" }}>
          Carica il PDF/Excel di un preventivo: l&apos;AI ne estrae materiali, posa e totale.
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onUpload(f);
            e.target.value = "";
          }}
        />
        <button
          className="btn btn-primary btn-sm"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        >
          <I.Upload size={13} /> {uploading ? "Caricamento…" : "Carica offerta"}
        </button>
      </div>

      <table className="tbl">
        <thead>
          <tr>
            <th>Fornitore</th>
            <th>Categoria</th>
            <th style={{ textAlign: "right", width: 130 }}>Materiali</th>
            <th style={{ textAlign: "right", width: 110 }}>Lavoro</th>
            <th style={{ textAlign: "right", width: 130 }}>Totale</th>
            <th style={{ textAlign: "right", width: 100 }}>vs Listino</th>
            <th style={{ width: 100 }}>Stato</th>
            <th style={{ width: 210 }}></th>
          </tr>
        </thead>
        <tbody>
          {loading && quotes.length === 0 ? (
            <tr>
              <td
                colSpan={8}
                style={{ textAlign: "center", padding: 28, color: "var(--c-ink-3)", fontSize: 13 }}
              >
                Caricamento offerte…
              </td>
            </tr>
          ) : quotes.length === 0 ? (
            <tr>
              <td
                colSpan={8}
                style={{ textAlign: "center", padding: 28, color: "var(--c-ink-3)", fontSize: 13 }}
              >
                Nessuna offerta — carica il file di un preventivo.
              </td>
            </tr>
          ) : (
            quotes.map((q) => (
              <QuoteRow
                key={q.id}
                q={q}
                onSetStatus={onSetStatus}
                onDelete={onDelete}
                onEditField={onEditField}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
